// ONE source of truth for the [variables] in branding texts (frontend).
// Used by the CRM (crm/src/mixins/stringMixin.js) and the customer site
// (client/src/mixins/stringMixin.js) — both mixins only map their app options
// onto the context below. Imported from the repo root like locales/*.json.
//
// The reservation e-mail body is rendered by the API
// (expressApi/src/email/reservationEmail.js buildBodyVars) and has no client
// locales / client URL — see VARIABLES_EMPTY_IN.reservationEmailBody.

// Every variable, in table order, with its English fallback description
// (the translated one comes from locales/*.json → variables.<key>).
export const TEMPLATE_VARIABLES = [
  { key: 'title', fallback: 'Title Header' },
  { key: 'firstname', fallback: 'First Name' },
  { key: 'name', fallback: 'Last Name' },
  { key: 'signatur', fallback: 'Signature' },
  { key: 'userN', fallback: 'Travel agent First name Name' },
  { key: 'userL', fallback: 'Travel agent Last name' },
  { key: 'userF', fallback: 'Travel agent full name' },
  { key: 'userE', fallback: 'Travel agent Email' },
  { key: 'userT', fallback: 'Travel agent Telephone' },
  { key: 'username', fallback: 'Travel agent full name (depracated will be remvoved soon)' },
  { key: 'userEmail', fallback: 'Travel agent email (depracated will be remvoved soon)' },
  { key: 'link', fallback: 'Link to Login' },
  { key: 'code', fallback: 'Access Code' },
  { key: 'trip_id', fallback: 'Trip reference ID' },
  { key: 'trip_alias', fallback: 'Trip name / alias' },
  { key: 'start_date', fallback: 'Trip start date' },
  { key: 'end_date', fallback: 'Trip end date' },
  { key: 'customer_firstname', fallback: 'Customer first name' },
  { key: 'customer_lastname', fallback: 'Customer last name' },
  { key: 'customer_email', fallback: 'Customer email' },
  { key: 'customer_phone', fallback: 'Customer phone' },
  { key: 'total_amount', fallback: 'Total amount' },
  { key: 'currency', fallback: 'Currency' },
  { key: 'checkout_ref_id', fallback: 'Checkout reference ID' },
  { key: 'booking_state', fallback: 'Booking state' },
];

// Only known after checkout — filled only in the reservation e-mail.
export const CHECKOUT_ONLY_VARIABLES = ['total_amount', 'currency', 'checkout_ref_id'];

// Per branding editor: variables that can NEVER be filled there (no data at that
// place), shown as a hint under the editor. Not "empty for this trip".
export const VARIABLES_EMPTY_IN = {
  subject: CHECKOUT_ONLY_VARIABLES,
  template: CHECKOUT_ONLY_VARIABLES,
  welcomeText: CHECKOUT_ONLY_VARIABLES,
  welcomeTextBooked: CHECKOUT_ONLY_VARIABLES,
  infoDocs: CHECKOUT_ONLY_VARIABLES,
  travelReqMsg: CHECKOUT_ONLY_VARIABLES,
  signatureSupplier: CHECKOUT_ONLY_VARIABLES,
  // API-rendered: no client locales (salutation) and no client URL.
  reservationEmailBody: ['title', 'name', 'link'],
};

// The signature is inserted into several texts and follows the rules of each.
export const VARIABLES_DEPEND_ON_HOST = ['signature'];

const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
// ISO YYYY-MM-DD[...] → DD.MM.YYYY; anything else unchanged.
const euroDate = (v) => {
  const m = String(v ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (v || '');
};
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');

/**
 * Values for every variable. Missing data → '' (never the raw "[key]").
 *
 * @param {object} ctx
 * @param {object} ctx.trip              parsed Nezasa itinerary ({ data: { id, attributes } })
 * @param {object} ctx.salutations       locales/<lang>.json → salutations ({ Mr, Mrs, customer, dear })
 * @param {object} [ctx.salutationConfig] branding_languages.texts.salutation (custom [title] wording)
 * @param {string} [ctx.signature]       branding signature (HTML, may contain variables)
 * @param {object} [ctx.reisedesigner]   travel expert ({ firstname, lastname, username, telefon })
 * @param {string} [ctx.tripUrl]         customer-site base URL for [link]
 * @param {string} [ctx.loginCode]       trip login code for [code]
 */
export function buildTemplateVariables (ctx = {}) {
  const trip = ctx.trip || null;
  const attrs = trip?.data?.attributes || {};
  const contact = attrs.pax?.contact || attrs.pax?.paxInfo?.[0];
  const gender = contact?.gender;
  const isMale = gender === 'Male';
  const isFemale = gender === 'Female';
  const lastName = contact?.lastName || '';
  const firstName = contact?.firstName || '';
  const hasName = lastName || firstName;
  const salutations = ctx.salutations || {};
  // Per-branding [title] override: a selected custom choice replaces the locale "dear" wording.
  const salConfig = ctx.salutationConfig;
  const salOverride = salConfig?.selected
    ? (Array.isArray(salConfig.choices) ? salConfig.choices : []).find(c => c && c.id === salConfig.selected)
    : null;
  const dear = salOverride
    ? { male: salOverride.male, female: salOverride.female, neutral: salOverride.neutral || `${salOverride.female}/${salOverride.male}` }
    : (salutations.dear || {});
  const neutralDear = dear.neutral || `${dear.female}/${dear.male}`;
  const conString = capitalize(lastName);
  const rd = ctx.reisedesigner || {};
  const rdFull = rd.firstname && rd.lastname ? `${rd.firstname} ${rd.lastname}` : '';

  return {
    title: !hasName ? neutralDear : isMale ? dear.male : isFemale ? dear.female : neutralDear,
    firstname: capitalize(firstName),
    name: !hasName ? (salutations.customer || 'Kunden') : isMale ? `${salutations.Mr} ${conString}` : isFemale ? `${salutations.Mrs} ${conString}` : `${salutations.Mrs}/${salutations.Mr} ${conString}`,
    signatur: ctx.signature ? String(ctx.signature).replace(/\[signatur\]/g, '') : '',
    userN: rd.firstname || '',
    userL: rd.lastname || '',
    userF: rdFull,
    username: rdFull, // will be removed
    userE: rd.username || '',
    userEmail: rd.username || '', // will be removed
    userT: rd.telefon || '',
    link: ctx.tripUrl && trip?.data?.id ? `<a href="${ctx.tripUrl}/angebot?trip=${trip.data.id}" target="_blank">Link</a>` : '',
    code: esc(ctx.loginCode),
    // Plain text from the Nezasa JSON → escaped, the result is rendered as HTML.
    trip_id: esc(trip?.data?.id),
    trip_alias: esc(attrs.alias),
    start_date: esc(euroDate(attrs.startDate)),
    end_date: esc(euroDate(attrs.endDate)),
    customer_firstname: esc(firstName),
    customer_lastname: esc(lastName),
    customer_email: esc(contact?.email),
    customer_phone: esc(contact?.mobilePhone || contact?.phone),
    booking_state: esc(attrs.bookingState),
    // Only exist after checkout (reservation e-mail).
    total_amount: '',
    currency: '',
    checkout_ref_id: '',
  };
}

/**
 * Variables pasted with code formatting (e.g. copied from a code-styled list)
 * end up in <code> / <pre><code>. The rich-text editor allows no links inside
 * code, so [link] would lose its <a>. Unwrap code that contains a known
 * variable; code without a variable is left alone.
 */
function stripCodeAroundVariables (html, keys) {
  const hasVariable = (s) => keys.some(k => s.includes(`[${k}]`));
  return html
    // code block → paragraph (line breaks kept)
    .replace(/<pre[^>]*>\s*<code[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi,
      (m, inner) => (hasVariable(inner) ? `<p>${inner.replace(/\r?\n/g, '<br>')}</p>` : m))
    // inline code → plain text
    .replace(/<code[^>]*>([\s\S]*?)<\/code>/gi,
      (m, inner) => (hasVariable(inner) ? inner : m));
}

/**
 * Replace every known [variable] in text. Repeats so variables inside inserted
 * values (e.g. the signature) are filled too. Unknown bracket text such as
 * "[hier klicken](url)" is left alone.
 */
export function renderTemplateVariables (text, ctx) {
  if (!text) return text;
  const vars = buildTemplateVariables(ctx);
  const MAX_ITERATIONS = 10;
  let result = stripCodeAroundVariables(String(text), Object.keys(vars));
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const prev = result;
    Object.entries(vars).forEach(([key, value]) => {
      result = result.split(`[${key}]`).join(value ?? '');
    });
    if (result === prev) break;
  }
  return result;
}
